/** Per-anchor scroll nudge after centering (landing sections tuned by eye). */
const ANCHOR_EXTRA_SCROLL_PX: Readonly<Record<string, number>> = {
  team: 288,
};

/** Scroll a page section into view, honoring reduced-motion preferences. */
export function scrollToElementId(id: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const target = document.getElementById(id);
  if (target === null) {
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = reducedMotion ? 'auto' : 'smooth';
  const extraScrollTop = ANCHOR_EXTRA_SCROLL_PX[id] ?? 0;

  scrollTopToVisuallyCenter(resolveScrollAnchor(target), behavior, extraScrollTop);
}

/** Element id from a same-page nav href (`#team`, `/#team`, or full URL with hash). */
export function elementIdFromHashHref(href: string): string | null {
  const hashIndex = href.indexOf('#');
  if (hashIndex === -1) {
    return null;
  }

  const id = href.slice(hashIndex + 1);
  return id.length > 0 ? id : null;
}

function resolveScrollAnchor(target: Element): Element {
  const scrollTarget = target.closest('section') ?? target;
  const viewportHeight = window.innerHeight;
  const sectionHeight = scrollTarget.getBoundingClientRect().height;

  if (sectionHeight < viewportHeight) {
    return scrollTarget;
  }

  // Tall sections: center the heading block (title + subtitle), not the whole section.
  return target.closest('header') ?? target;
}

function readHeaderChromeHeightPx(): number {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--app-header-chrome-height')
    .trim();

  if (raw === '') {
    return 0;
  }

  if (raw.endsWith('px')) {
    return Number.parseFloat(raw);
  }

  if (raw.endsWith('rem')) {
    const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
    return Number.parseFloat(raw) * rootFontSize;
  }

  return 0;
}

/** Centers an element in the viewport area below the fixed app header. */
function scrollTopToVisuallyCenter(
  element: Element,
  behavior: ScrollBehavior,
  extraScrollTop: number = 0,
): void {
  const rect = element.getBoundingClientRect();
  const absoluteTop = window.scrollY + rect.top;
  const elementHeight = rect.height;
  const viewportHeight = window.innerHeight;
  const headerOffset = readHeaderChromeHeightPx();

  const visibleCenterY = headerOffset + (viewportHeight - headerOffset) / 2;
  const elementCenterY = absoluteTop + elementHeight / 2;
  const top = elementCenterY - visibleCenterY + extraScrollTop;

  const maxScroll = document.documentElement.scrollHeight - viewportHeight;
  const clampedTop = Math.min(Math.max(0, top), maxScroll);

  window.scrollTo({ top: clampedTop, behavior });
}
